<template>
  <div>
    <!-- Banner principale -->
    <transition name="slide-up">
      <div v-if="cookieStore.showBanner" class="cookie-banner">
        <div class="cookie-container">
          <!-- Testo -->
          <div class="cookie-content">
            <h3 class="cookie-title">{{ t('cookies.banner.title') }}</h3>
            <p class="cookie-text">
              {{ t('cookies.banner.text') }}
            </p>
            <router-link to="/cookie-policy" class="cookie-link">{{ t('footer.nav.legal.cookie') }}</router-link>
          </div>

          <!-- Azioni -->
          <div class="cookie-actions">
            <button class="btn-settings" @click="cookieStore.showSettings = true">
              {{ t('cookies.banner.btn_manage') }}
            </button>
            <button class="btn-reject" @click="cookieStore.rejectAll()">
              {{ t('cookies.banner.btn_reject') }}
            </button>
            <button class="btn-accept" @click="cookieStore.acceptAll()">
              {{ t('cookies.banner.btn_accept') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal Impostazioni (Svincolato dal banner per permettere l'apertura manuale da altre pagine) -->
    <transition name="fade">
      <div v-if="cookieStore.showSettings" class="cookie-settings-modal">
        <div class="settings-box">
          <button class="close-btn" @click="cookieStore.showSettings = false">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <h2 class="settings-title">{{ t('cookies.modal.title') }}</h2>
          <p class="cookie-text" style="margin-bottom: 20px;">{{ t('cookies.modal.subtitle') }}</p>
          
          <!-- Cookie Essenziali -->
          <div class="cookie-group">
            <div class="cookie-group-header" @click="toggleGroup('essential')">
              <input 
                type="checkbox" 
                id="essential" 
                name="essential_cookies"
                :checked="cookieStore.preferences.essential"
                disabled
                class="checkbox"
                @click.stop
              >
              <label for="essential" class="group-label" @click.stop>
                <span class="group-name">{{ t('cookies.cat.necessary.title') }}</span>
                <span class="required-badge">{{ t('cookies.cat.necessary.status') }}</span>
              </label>
              <span class="chevron" :class="{ 'open': activeGroup === 'essential' }">▼</span>
            </div>
            <transition name="collapse">
              <div v-if="activeGroup === 'essential'" class="group-details">
                <p class="group-description">
                  {{ t('cookies.cat.necessary.desc') }}
                </p>
                <ul class="cookie-list">
                  <li><strong>PHPSESSID</strong>: {{ t('cookies.desc.phpsessid') }}</li>
                  <li><strong>cookie-preferences</strong>: {{ t('cookies.desc.preferences') }}</li>
                  <li><strong>consent-id</strong>: {{ t('cookies.desc.consent_id') }}</li>
                </ul>
              </div>
            </transition>
          </div>

          <!-- Cookie di Analisi -->
          <div class="cookie-group">
            <div class="cookie-group-header" @click="toggleGroup('analytics')">
              <input 
                type="checkbox" 
                id="analytics" 
                name="analytics_cookies"
                v-model="cookieStore.preferences.analytics"
                class="checkbox"
                @click.stop
              >
              <label for="analytics" class="group-label" @click.stop>
                {{ t('cookies.cat.analytics.title') }}
              </label>
              <span class="chevron" :class="{ 'open': activeGroup === 'analytics' }">▼</span>
            </div>
            <transition name="collapse">
              <div v-if="activeGroup === 'analytics'" class="group-details">
                <p class="group-description">
                  {{ t('cookies.cat.analytics.desc') }}
                </p>
                <ul class="cookie-list">
                  <li><strong>_ga, _gid, _ga_*</strong>: {{ t('cookies.desc.ga') }}</li>
                </ul>
              </div>
            </transition>
          </div>

          <!-- Cookie di Marketing -->
          <div class="cookie-group">
            <div class="cookie-group-header" @click="toggleGroup('marketing')">
              <input 
                type="checkbox" 
                id="marketing" 
                name="marketing_cookies"
                v-model="cookieStore.preferences.marketing"
                class="checkbox"
                @click.stop
              >
              <label for="marketing" class="group-label" @click.stop>
                {{ t('cookies.cat.marketing.title') }}
              </label>
              <span class="chevron" :class="{ 'open': activeGroup === 'marketing' }">▼</span>
            </div>
            <transition name="collapse">
              <div v-if="activeGroup === 'marketing'" class="group-details">
                <p class="group-description">
                  {{ t('cookies.cat.marketing.desc') }}
                </p>
                <ul class="cookie-list">
                  <li><strong>_fbp</strong>: {{ t('cookies.desc.meta') }}</li>
                </ul>
              </div>
            </transition>
          </div>

          <!-- Pulsanti -->
          <div class="settings-actions">
            <button class="btn-cancel" @click="cookieStore.showSettings = false">
              {{ t('cookies.modal.btn_back') }}
            </button>
            <button class="btn-save" @click="cookieStore.savePreferences(cookieStore.preferences)">
              {{ t('cookies.modal.btn_save') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue'
import { useCookieStore } from '@/stores/useCookieStore'
import { useLanguage } from '@/composables/useLanguage'

const cookieStore = useCookieStore()
const { t } = useLanguage()
const activeGroup = ref(null)

const toggleGroup = (group) => {
  activeGroup.value = activeGroup.value === group ? null : group
}

// Handle scroll lock on body when settings modal is open
watch(() => cookieStore.showSettings, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
    document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`
  } else {
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.width = ''
    document.body.style.paddingRight = ''
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
  document.body.style.position = ''
  document.body.style.width = ''
  document.body.style.paddingRight = ''
})
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  border-top: 1px solid #eee;
  padding: 20px 40px;
  z-index: 999;
}

.cookie-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 30px;
  justify-content: space-between;
}

.cookie-content {
  flex: 1;
}

.cookie-title {
  color: #111;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.cookie-text {
  color: #666;
  font-size: 12px;
  line-height: 1.6;
  margin: 0 0 8px 0;
}

.cookie-link {
  color: #111;
  text-decoration: underline;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cookie-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

button {
  padding: 12px 20px;
  font-size: 11px;
  font-weight: 700;
  border: none;
  border-radius: 0;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  outline: none !important;
}

.btn-settings {
  background-color: transparent;
  border: 1px solid #ccc;
  color: #666;
}

.btn-settings:hover {
  border-color: #111;
  color: #111;
}

.btn-reject {
  background-color: transparent;
  border: 1px solid #ccc;
  color: #666;
}

.btn-reject:hover {
  border-color: #111;
  color: #111;
}

.btn-accept {
  background-color: #111;
  color: #fff;
}

.btn-accept:hover {
  background-color: #000;
}

/* Transizioni */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.3s ease-out, opacity 0.2s ease;
  max-height: 500px;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.cookie-settings-modal {
  position: fixed;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.settings-box {
  background-color: #fff;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 100;
}

.close-btn:hover {
  color: #111;
  transform: scale(1.1);
}

.settings-title {
  color: #111;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 30px 0;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.cookie-group {
  margin-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.cookie-group-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 0;
  cursor: pointer;
}

.checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #111;
  outline: none !important;
}

.group-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  flex: 1;
}

.required-badge {
  background-color: #eee;
  color: #999;
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 0;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.chevron {
  color: #ccc;
  font-size: 10px;
  transition: transform 0.3s ease;
}

.chevron.open {
  transform: rotate(180deg);
}

.group-details {
  padding: 0 0 20px 30px;
}

.group-description {
  color: #666;
  font-size: 12px;
  margin: 0 0 10px 0;
  line-height: 1.6;
}

.cookie-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.cookie-list li {
  font-size: 11px;
  color: #999;
  margin-bottom: 5px;
}

.cookie-list strong {
  color: #111;
}

.settings-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 30px;
}

.settings-actions .btn-save {
  background-color: #111;
  color: #fff;
}

.settings-actions .btn-cancel {
  background-color: transparent;
  color: #999;
}

@media (max-width: 1024px) {
  .cookie-banner {
    padding: 20px;
  }

  .cookie-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .cookie-actions {
    width: 100%;
    flex-direction: column;
  }

  button {
    width: 100%;
  }

  /* Modal Mobile Adjustments - Forzato per tutti i mobile anche in landscape */
  .cookie-settings-modal {
    padding: 0 !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    overflow-y: auto !important;
    background-color: #ffffff !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    z-index: 999999 !important;
    display: block !important; /* Rimuove flex per permettere scroll naturale */
  }

  .settings-box {
    padding: 80px 20px 100px !important; /* Spazio extra in fondo per i tasti sticky */
    width: 100% !important;
    max-width: 100% !important;
    min-height: 100% !important;
    max-height: none !important;
    margin: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    background-color: #fff !important;
  }

  .close-btn {
    position: fixed !important;
    top: 0 !important;
    right: 0 !important;
    width: 60px !important;
    height: 60px !important;
    background: #ffffff !important;
    border-bottom: 1px solid #eee !important;
    border-left: 1px solid #eee !important;
    border-radius: 0 !important;
    z-index: 1000000 !important;
  }

  .settings-actions {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    background: #ffffff !important;
    padding: 15px 20px !important;
    margin: 0 !important;
    border-top: 1px solid #eee !important;
    flex-direction: row !important;
    gap: 10px !important;
    z-index: 1000001 !important;
  }
  
  .settings-actions button {
    flex: 1 !important;
    margin: 0 !important;
  }
}
</style>
